/*
 * Typed REST client for the Cogent Team control plane.
 *
 * Pattern: a thin RemoteDataSource (vault: RemoteDataSource / HttpClient) — every
 * endpoint module (auth.ts, admin.ts, channels.ts) calls `apiFetch<T>` which
 * centralises base URL, JSON, the Bearer auth header, a request timeout, and error
 * translation. A promisified fetch helper must consolidate THREE failure sources
 * (vault getJSON): (1) comms failure, (2) unanticipated status, (3) invalid body.
 * fetch() only rejects on a NETWORK failure — an HTTP 4xx/5xx still FULFILLS — and
 * a stalled connection NEVER settles, so we (a) check `res.ok` and throw ApiError,
 * and (b) impose a timeout via AbortController (vault: Ninja Listing 8.5) so a hung
 * server can't leave the promise pending forever.
 */
import type { ZodType } from "zod";

const BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
// Default per-request timeout. Configurable, but bounded by default so no request
// can hang indefinitely (vault Listing 8.5: without an abort the fetch keeps
// running in the background, consuming resources).
const DEFAULT_TIMEOUT_MS: number = Number(import.meta.env.VITE_API_TIMEOUT_MS ?? 30_000);

/** The control plane's standard error envelope (api/src/http/errors.ts). */
interface ApiErrorBody {
  success?: false;
  error?: string;
  message?: string;
  suggestion?: string;
}

/** Non-2xx HTTP response carrying the API's `{ error, message, suggestion }` envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly suggestion: string | undefined;
  constructor(status: number, code: string, message: string, suggestion?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.suggestion = suggestion;
  }
}

/** A network/offline failure (fetch rejected before any response). */
export class NetworkError extends Error {
  constructor(message = "Could not reach the server. Check your connection and try again.") {
    super(message);
    this.name = "NetworkError";
  }
}

/** Our request-timeout fired — the server accepted the connection but never responded. */
export class TimeoutError extends Error {
  constructor(message = "The server took too long to respond. Please try again.") {
    super(message);
    this.name = "TimeoutError";
  }
}

/** A 2xx body failed runtime schema validation — the wire shape drifted from the contract. */
export class ContractError extends Error {
  readonly cause: unknown;
  constructor(message = "The server returned data in an unexpected shape.", cause?: unknown) {
    super(message);
    this.name = "ContractError";
    this.cause = cause;
  }
}

// --- Bearer token store: sessionStorage (cleared when the tab closes; smaller
//     XSS blast radius than localStorage). One key, one source of truth. ---
const TOKEN_KEY = "cogent.token";

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage disabled — auth simply won't persist across reloads */
  }
}
export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* no-op */
  }
}

// --- 401 hook: the AuthContext registers a handler so an expired/invalid token
//     anywhere triggers a single logout+redirect, not ad-hoc handling per call. ---
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON body — serialised automatically. */
  body?: unknown;
  /** Attach the Bearer token (required for /admin and /billing/checkout). */
  auth?: boolean;
  /** Override the bearer used when `auth` is true (e.g. the OPERATOR realm's token).
   *  When provided it is used verbatim instead of the admin token store; pass `null`
   *  to deliberately send no token. Omitted → the admin token store (default realm). */
  token?: string | null;
  /** Called on a 401 for an authenticated request INSTEAD of the global admin logout
   *  (so a second realm — the operator console — clears its OWN token, not the admin's). */
  onAuthError?: () => void;
  /** Caller cancellation (e.g. TanStack Query's signal on unmount/refetch). */
  signal?: AbortSignal;
  /** Per-request timeout override (ms). Defaults to DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number;
}

/**
 * Compose an internal timeout signal with the caller's signal. Prefers the native
 * `AbortSignal.any`; falls back to forward-aborting when it's unavailable. Accessed
 * via a cast so this compiles regardless of the lib's DOM typings version.
 */
function composeSignal(caller: AbortSignal | undefined, timeoutSignal: AbortSignal, timeoutCtl: AbortController): AbortSignal {
  const anyFn = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === "function") {
    return anyFn(caller ? [caller, timeoutSignal] : [timeoutSignal]);
  }
  if (caller) {
    if (caller.aborted) timeoutCtl.abort();
    else caller.addEventListener("abort", () => timeoutCtl.abort(), { once: true });
  }
  return caller ?? timeoutSignal;
}

export async function apiFetch<T>(
  path: string,
  opts: ApiRequestOptions & { schema?: ZodType<T> } = {},
): Promise<T> {
  const { method = "GET", body, auth = false, signal, timeoutMs = DEFAULT_TIMEOUT_MS, schema } = opts;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    // A realm may override the token (operator console); otherwise the admin store.
    const token = opts.token !== undefined ? opts.token : getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  // Timeout via AbortController (vault Ninja Listing 8.5). Composed with the
  // caller's signal so either source aborts the request; the timer is always
  // cleared in `finally` (vault getJSON cleanup discipline).
  const timeoutCtl = new AbortController();
  const timer = setTimeout(() => timeoutCtl.abort(), timeoutMs);
  const composed = composeSignal(signal, timeoutCtl.signal, timeoutCtl);

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: composed,
    });
  } catch (err) {
    // Our timeout fired (and the caller didn't cancel) → a distinct, messageable error.
    if (timeoutCtl.signal.aborted && !signal?.aborted) throw new TimeoutError();
    // A caller/Query cancel (unmount/refetch) → rethrow the raw AbortError so
    // Query's cancellation semantics keep working.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new NetworkError();
  } finally {
    clearTimeout(timer);
  }

  // 204 No Content (e.g. DELETE /admin/members/:id, DELETE /admin/channels/:id).
  if (res.status === 204) return undefined as T;

  const responseText = await res.text();
  const data: unknown = responseText ? safeJson(responseText) : null;

  if (!res.ok) {
    const envelope = (data ?? {}) as ApiErrorBody;
    const code = envelope.error ?? `HTTP_${res.status}`;
    const message = displayMessage(envelope, res.status, res.statusText);
    // ANY 401 on an AUTHENTICATED request means a dead session — expired, bad
    // signature, wrong issuer (e.g. after a JWT-secret rotation), or missing —
    // so clear the token and fire one global logout. We gate on `auth` (not the
    // specific code) so the unauthenticated flows (login/register/verify) never
    // auto-logout on their own 401s (wrong credentials, used link, etc.).
    if (res.status === 401 && auth) {
      if (opts.onAuthError) {
        // A second realm (operator console) clears its OWN token + redirects; the
        // admin token store + global handler are left untouched.
        opts.onAuthError();
      } else {
        clearToken();
        onUnauthorized?.();
      }
    }
    throw new ApiError(res.status, code, message, envelope.suggestion);
  }

  // Success body: validate at the boundary when a schema is given (vault
  // Type_Assertion — prefer runtime validation over an unchecked `as T` for API
  // responses), else keep the typed-cast path for callers that opt out.
  if (!schema) return data as T;
  const parsed = schema.safeParse(data);
  if (!parsed.success) throw new ContractError(undefined, parsed.error);
  return parsed.data;
}

/**
 * The API's shared error formats `message` as `${code}: ${message}. ${suggestion}`
 * (BridgeError). We already have `error` (code) and `suggestion` as separate fields,
 * so strip those structured parts deterministically and surface only the human
 * sentence — no machine prefix, no doubled suggestion. Never returns an empty
 * string (HTTP/2 has no statusText, so the old `?? "Request failed"` left it blank).
 */
function displayMessage(envelope: ApiErrorBody, status: number, statusText: string): string {
  let msg = envelope.message || statusText || `Request failed (HTTP ${status})`;
  if (envelope.error && msg.startsWith(`${envelope.error}: `)) {
    msg = msg.slice(envelope.error.length + 2);
  }
  if (envelope.suggestion && msg.endsWith(`. ${envelope.suggestion}`)) {
    msg = msg.slice(0, msg.length - envelope.suggestion.length - 2);
  }
  return msg.trim() || `Request failed (HTTP ${status})`;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
